import React, { useEffect, useRef, useState } from "react";
import { TextField, InputAdornment } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { AppCountrySelectProps } from "../UI/AppSelectOptions/AppSelect.props";
const AppCountrySelect = ({
  label,
  fieldKey,
  value,
  options = [],
  required = false,
  readOnly = false,
  translate,
  formStates,
  setUserDetails,
  cityFieldName,
  stateFieldName,
  onChange,
  enableUppercaseInputField = false,
}: AppCountrySelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(value || "");

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchValue(value || "");
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (!event.target.closest(".custom-dropdown-container")) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option: string) =>
    option.toLowerCase().startsWith(searchValue.toLowerCase())
  );

  //  SELECT HANDLER
  const handleSelect = (option: string) => {
    setSearchValue(option);
    setIsOpen(false);

    // update form
    formStates.setValue(fieldKey, option, {
      shouldValidate: true,
    });

    // update local state
    setUserDetails((prev: any) => ({
      ...prev,
      [fieldKey]: option,
    }));

    if (formStates) {
      if (cityFieldName) {
        formStates.setValue(cityFieldName, "");
      }
      if (stateFieldName) {
        formStates.setValue(stateFieldName, "");
      }
    }

    if (onChange) {
      onChange(option);
    }
  };

  return (
    <div
      className="custom-dropdown-container relative w-full text-sm sm:text-base"
      ref={dropdownRef}
    >
      <TextField
        fullWidth
        required={required}
        disabled={readOnly}
        label={translate(label)}
        value={searchValue || ""}
        onChange={(e) => {
          const val = enableUppercaseInputField
            ? e.target.value.toUpperCase()
            : e.target.value;

          setSearchValue(val);
          setIsOpen(true);

          if (val === "") {
            formStates.setValue(fieldKey, "", {});

            setUserDetails((prev: any) => ({
              ...prev,
              [fieldKey]: "",
            }));
          }
        }}
        onFocus={() => {
          if (!readOnly) setIsOpen(true);
        }}
        inputProps={{
          style: enableUppercaseInputField
            ? { textTransform: "uppercase" }
            : {},
        }}
        InputProps={{
          sx: {
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "16px",
          },
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon
                style={{
                  cursor: readOnly ? "default" : "pointer",
                  transition: "0.2s",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
                onClick={() => {
                  if (!readOnly) setIsOpen((prev) => !prev);
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      {/* DROPDOWN */}
      {isOpen && !readOnly && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option: string, index: number) => (
              <div
                key={index}
                className="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  handleSelect(option);
                }}
              >
                <span
                  className="block truncate"
                  style={{
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  }}
                >
                  {option}
                </span>
              </div>
            ))
          ) : (
            <div className="text-gray-900 py-2 pl-3 pr-9">
              {translate("No options")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppCountrySelect;
